'use client'
import { useTranslation } from 'next-i18next'
import getConfig from '@/config'
import React, { useEffect, useState } from 'react'

import {
  calculateScoreAndPassStatus,
  LearningModuleRevisionType,
  LearningSessionScore,
  SeriesURLParts,
} from '../../../../graphql/learning'
import { useModulesInSeriesPassed } from '../../../../graphql/learning/hooks'

import { isNativeAppBrowser } from '../../../../lib/client-info'
import { useUser } from '../../../../modules/auth/hooks'
import Loading from '../../../../ui/loading'
import { postNotification } from '../../../../modules/notify/actions'
import { useDispatch } from '../../../../react-redux'
import callAPI from '../../../../modules/api/call'
import useForm from '../../../Forms/useForm'

const config = getConfig().publicRuntimeConfig

const buildCertificateUrls = (series: SeriesURLParts, module_id: string) => {
  const htmlUrl = `${config.mainSiteUrl}/learning/groups/${series.library_id}/${series.id}/${module_id}/certificate?pdf=1`
  return {
    pdfUrl: `${config.pdfUrl}?src=${encodeURIComponent(htmlUrl)}&waitForFunction=${encodeURIComponent('window.readyToPrint === true')}`,
    emailUrl: `${config.mainSiteUrl}/api/learning/groups/${series.library_id}/series/${series.id}/module/${module_id}/send-to-email`,
  }
}

const hasPassedModule = (
  session: LearningSessionScore,
  revision: LearningModuleRevisionType
) =>
  !revision.mcq_settings_enabled ||
  revision.mcq_settings_pass_mark_type === 'none' ||
  session.status === 'no_pass_mark' ||
  calculateScoreAndPassStatus(session, revision)?.isPass

export const CertificateLinkForSeries: React.FC<{
  series: SeriesURLParts
  certificateUrl?: string
  button?: boolean
}> = ({ series, certificateUrl, button }) => {
  if (series.accreditation_when !== 'series') return null

  const { hasPassedAll } = useModulesInSeriesPassed(series.id)
  if (!hasPassedAll) return null

  const urls = buildCertificateUrls(series, '_')
  return (
    <CertificateAnchor
      pdfUrl={certificateUrl || urls.pdfUrl}
      emailUrl={urls.emailUrl}
      button={button}
    />
  )
}

export const CertificateLinkForRevisionSession: React.FC<{
  series: SeriesURLParts
  session: LearningSessionScore
  revision: LearningModuleRevisionType
  certificateUrl?: string
  button?: boolean
}> = ({ series, session, revision, certificateUrl, button }) => {
  if (series.accreditation_when !== 'module') return null
  if (!hasPassedModule(session, revision)) return null

  const urls = buildCertificateUrls(series, revision.module_id)
  return (
    <CertificateAnchor
      pdfUrl={certificateUrl || urls.pdfUrl}
      emailUrl={urls.emailUrl}
      button={button}
    />
  )
}

const CertificateAnchor: React.FC<{
  pdfUrl: string
  emailUrl: string
  button?: boolean
}> = ({ pdfUrl, emailUrl, button }) => {
  const { t } = useTranslation('learning')
  const dispatch = useDispatch()
  const [sending, setSending] = useState(false)

  if (isNativeAppBrowser()) {
    const sendByEmail = () => {
      setSending(true)
      callAPI({ url: emailUrl, method: 'GET' })
        .then(() => {
          dispatch(
            postNotification({
              kind: 'info',
              message:
                'Email with a link to the certificate has been sent to your email.',
              closeBtn: false,
            })
          )
        })
        .catch(() => {
          dispatch(
            postNotification({
              kind: 'error',
              message: 'Something went wrong.',
              closeBtn: false,
            })
          )
        })
        .finally(() => setSending(false))
    }

    return sending ? (
      <Loading />
    ) : (
      <a
        className={button ? 'btn' : undefined}
        style={button ? undefined : { textDecoration: 'underline' }}
        onClick={sendByEmail}
      >
        {t('learning.send_cert_by_email')}
      </a>
    )
  }

  return (
    <a
      className={
        button ? 'btn btn--print-certificate' : 'link--print-certificate'
      }
      href={pdfUrl}
      target="_blank"
      rel="noreferrer noopener"
    >
      {t('learning.download_your_certificate')}
    </a>
  )
}

/**
 * Main entry point: checks feedback form requirements, then renders
 * the appropriate certificate link based on accreditation_when.
 */
const DownloadCertificateLink: React.FC<{
  series: SeriesURLParts
  session: LearningSessionScore
  revision: LearningModuleRevisionType
  certificateUrl?: string
  button?: boolean
}> = ({ series, session, revision, certificateUrl, button }) => {
  const currentUser = useUser()

  const hasCustomFeedbackForm = !!(
    revision.settings?.custom_feedback_form_active && revision?.feedback_form_id
  )
  const { form: feedbackForm, form_responses } = hasCustomFeedbackForm
    ? useForm(revision?.feedback_form_id)
    : { form: null, form_responses: null }

  const [alreadySubmitted, setAlreadySubmitted] = useState(false)
  useEffect(() => {
    setAlreadySubmitted(form_responses?.length > 0)
  }, [form_responses])

  const hasRequiredFields = feedbackForm?.fields.some(field => field?.required)
  if (hasCustomFeedbackForm && !alreadySubmitted && hasRequiredFields) {
    return null
  }

  if (series.accreditation_when === 'series') {
    return (
      <CertificateLinkForSeries
        series={series}
        certificateUrl={certificateUrl}
        button={button}
      />
    )
  }

  if (series.accreditation_when === 'module') {
    return (
      <CertificateLinkForRevisionSession
        series={series}
        session={session}
        revision={revision}
        certificateUrl={certificateUrl}
        button={button}
      />
    )
  }

  return null
}

export default DownloadCertificateLink
